import React, { createContext, useState, useContext, useEffect } from 'react';

// Create the cart context
const CartContext = createContext();

// Session storage keys
const CART_STORAGE_KEY = 'cart';
const CART_TIMESTAMP_KEY = 'cart_timestamp';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Load cart from localStorage on component mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
      setInitialized(true);
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      setInitialized(true);
    }
  }, []);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/products`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products for cart:', error);
      // Fallback to hardcoded products if API fails
      setProducts([
        {
          id: 'basic',
          name: 'Basic Memorial Package',
          description: 'Single stainless steel QR code, perfect for mounting on a gravestone.',
          price: 75.00,
          image: '/qr-basic.jpg',
        },
        {
          id: 'family',
          name: 'Family Memorial Package',
          description: 'Complete memorial solution with gravestone QR code and heart-shaped keyrings for family members.',
          price: 150.00,
          image: '/qr-family.jpg',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Initial product fetch
  useEffect(() => {
    fetchProducts();
  }, []);

  // Refresh products periodically if cart has items
  useEffect(() => {
    if (Object.keys(cart).length === 0) return;
    
    const refreshInterval = setInterval(() => {
      fetchProducts();
    }, 60000); // Refresh every minute
    
    return () => clearInterval(refreshInterval);
  }, [cart]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    // Only save after initial load to prevent clearing cart during initialization
    if (initialized) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      localStorage.setItem(CART_TIMESTAMP_KEY, Date.now().toString());
    }
  }, [cart, initialized]);

  // Add item to cart
  const addToCart = (productId, quantity = 1) => {
    setCart(prevCart => {
      const currentQuantity = prevCart[productId] || 0;
      return {
        ...prevCart,
        [productId]: currentQuantity + quantity
      };
    });
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    setCart(prevCart => {
      const updatedCart = { ...prevCart };
      delete updatedCart[productId];
      return updatedCart;
    });
  };

  // Update item quantity
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart => ({
        ...prevCart,
        [productId]: quantity
      }));
    }
  };

  // Clear the entire cart
  const clearCart = () => {
    setCart({});
  };
  
  // Restore cart from backup (used when returning from external sites)
  const restoreCart = (backupCart) => {
    if (backupCart && typeof backupCart === 'object') {
      setCart(backupCart);
      return true;
    }
    return false;
  };

  // Get total number of items in cart
  const getTotalItems = () => {
    return Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);
  };

  // Get cart items with product details - always uses latest product data
  const getCartItems = () => {
    return Object.entries(cart).map(([id, quantity]) => {
      // Find product in fetched products
      const product = products.find(p => p.id === id);
      
      // If product not found, return basic info
      if (!product) {
        return {
          id,
          name: 'Loading...',
          price: 0,
          quantity,
          description: '',
          image: null
        };
      }
      
      return {
        ...product,
        quantity
      };
    });
  };

  // Calculate subtotal using latest prices
  const calculateSubtotal = () => {
    return getCartItems().reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  // Calculate shipping
  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    // Free shipping on orders over £100
    return subtotal >= 100 ? 0 : 9.99;
  };

  // Calculate tax
  const calculateTax = () => {
    return calculateSubtotal() * 0.07; // 7% tax
  };

  // Calculate total
  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping() + calculateTax();
  };

  // Manual refresh for products (can be called when returning to cart)
  const refreshProducts = () => {
    fetchProducts();
  };

  // Context value
  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    restoreCart,
    refreshProducts,
    getTotalItems,
    getCartItems,
    calculateSubtotal,
    calculateShipping,
    calculateTax,
    calculateTotal,
    initialized
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;